import { useCallback, useEffect, useRef, useState } from 'react';
import { WebSocketClient, type WebSocketStatus } from '../lib/websocket';
import type { ClientMessage, ServerMessage } from '../types/protocol';

export interface UseWebSocketOptions {
  url: string;
  onMessage: (message: ServerMessage) => void;
  autoConnect?: boolean;
}

export interface UseWebSocketReturn {
  status: WebSocketStatus;
  connect: () => void;
  disconnect: () => void;
  send: (message: ClientMessage) => void;
}

export function useWebSocket({ url, onMessage, autoConnect = false }: UseWebSocketOptions): UseWebSocketReturn {
  const [status, setStatus] = useState<WebSocketStatus>('disconnected');
  const clientRef = useRef<WebSocketClient | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep callback ref current without re-creating the client
  onMessageRef.current = onMessage;

  useEffect(() => {
    const client = new WebSocketClient({
      url,
      onMessage: (msg) => onMessageRef.current(msg),
      onStatusChange: setStatus,
    });
    clientRef.current = client;

    if (autoConnect) {
      client.connect();
    }

    return () => {
      client.disconnect();
    };
  }, [url, autoConnect]);

  const connect = useCallback(() => {
    clientRef.current?.connect();
  }, []);

  const disconnect = useCallback(() => {
    clientRef.current?.disconnect();
  }, []);

  const send = useCallback((message: ClientMessage) => {
    clientRef.current?.send(message);
  }, []);

  return { status, connect, disconnect, send };
}
